import { Component, computed, effect, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";

import { NgClass } from "@angular/common";
import { KpiComponent } from "../../shared/components/kpi/kpi.component";
import { TableModule } from "@openng/optimus-ui/table";
import { TranslateModule } from "@ngx-translate/core";
import { ASSET_TYPES } from "../../enums/assetTypes.enum";
import { GateFaultTableComponent } from "../../shared/widgets/gate-fault-table/gate-fault-table.component";
import { ServiceListComponent } from "../../shared/widgets/service-list/service-list.component";
import { CustomerMapComponent } from "../../shared/widgets/customer-map/customer-map.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { CustomersFacadeService } from "./facade/customers-facade.service";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { SimplebarAngularModule } from "simplebar-angular";
import { EnrichedCustomer } from "../../models/api/asset/customer";
import { Column } from "../../models/ui/tableColumn";

@Component({
  standalone: true,
  selector: "app-customers",
  imports: [
    NgClass,
    KpiComponent,
    TranslateModule,
    TableModule,
    GateFaultTableComponent,
    ServiceListComponent,
    CustomerMapComponent,
    ProgressSpinnerModule,
    HasPermissionDirective,
    TimeoutComponent,
    SimplebarAngularModule,
  ],
  templateUrl: "./customers.component.html",
  styleUrl: "./customers.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [CustomersFacadeService],
})
export class CustomersComponent implements OnInit, OnDestroy {
  public selectedCustomer!: EnrichedCustomer;
  public assetTypes = ASSET_TYPES;
  public permissions = inject(PermissionService);
  public customersFacade = inject(CustomersFacadeService);

  public customers = computed(() => {
    const customers = this.customersFacade.customers();
    if (customers) {
      return customers;
    } else {
      return [];
    }
  });

  public customersLoading = computed(() => {
    return this.customersFacade.customers() === null;
  });
  public customersTimeout = createTimeoutSignal(this.customersLoading);
  public COLUMNS: Column[] = [
    {
      field: "label",
      header: "customers.table.label",
    },
    {
      field: "readyGateSystems",
      header: "customers.table.ready_gate_systems",
    },
    {
      field: "majorGateSystems",
      header: "customers.table.minor_gate_systems",
    },
    {
      field: "criticalGateSystems",
      header: "customers.table.major_gate_systems",
    },
    {
      field: "openGateSystems",
      header: "customers.table.open_gate_systems",
    },
    {
      field: "partiallyOpenGateSystems",
      header: "customers.table.partially_open_gate_systems",
    },
    {
      field: "closedGateSystems",
      header: "customers.table.closed_gate_systems",
    },
    {
      field: "numberOfOpenServices",
      header: "customers.table.number_open_services",
    },
  ];

  private initialCustomerSelect = false;

  constructor() {
    effect(() => {
      if (this.customers().length > 0 && !this.initialCustomerSelect) {
        this.selectCustomer(this.customers()[0]);
        this.initialCustomerSelect = true;
      }
    });
  }
  public ngOnInit() {
    this.customersFacade.loadInitialState();
  }
  public ngOnDestroy(): void {
    this.customersFacade.destroy();
  }
  /**
   * Sets the selected customer to the given customer and notifies the customersFacade.
   * @param customer The customer to select.
   */
  public selectCustomer(customer: EnrichedCustomer) {
    this.selectedCustomer = customer;
    this.customersFacade.selectCustomer(customer);
  }
}
